class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeUpsellQuickView = null;
    this.activeUpsellQuickViewOpener = null;
    this.upsellMediaPlaybackToken = 0;
    this.reducedMotionQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };
    this.saveDataEnabled = Boolean(
      navigator.connection && navigator.connection.saveData
    );
    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    this.upsellImpressionObserver = null;
    this.upsellImpressionTimers = new Map();
    this.upsellIntersectionRatios = new WeakMap();
    this.upsellExposures = this.getUpsellExposureStore();
    this.cartRenderObserver = null;

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;

      const activeQuickView = this.querySelector(
        '.cart-drawer__qv.is-open'
      );

      if (activeQuickView) {
        evt.preventDefault();
        evt.stopPropagation();
        this.closeUpsellQuickView(activeQuickView, true);
        return;
      }

      this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  connectedCallback() {
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    this.observeCartDrawerRenders();
  }

  disconnectedCallback() {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    this.stopUpsellImpressionTracking();

    if (this.cartRenderObserver) {
      this.cartRenderObserver.disconnect();
      this.cartRenderObserver = null;
    }
  }

  onVisibilityChange() {
    if (document.visibilityState !== 'hidden') {
      if (this.classList.contains('active')) {
        this.observeUpsellImpressions();
      }

      return;
    }

    this.stopUpsellImpressionTracking();

    const quickView =
      this.activeUpsellQuickView ||
      this.querySelector('.cart-drawer__qv.is-open');

    if (quickView) {
      this.pauseUpsellQuickViewMedia(quickView);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.observeUpsellImpressions();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains(
          'is-empty'
        )
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const activeQuickView = this.querySelector(
      '.cart-drawer__qv.is-open'
    );

    if (activeQuickView) {
      this.closeUpsellQuickView(activeQuickView, false);
    }

    this.stopUpsellImpressionTracking();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget
          .closest('details')
          .hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  onClick(event) {
    const quickViewOpen = event.target.closest(
      '[data-cart-upsell-quickview-open]'
    );

    if (quickViewOpen) {
      event.preventDefault();
      event.stopPropagation();

      this.openUpsellQuickView(
        quickViewOpen.dataset.cartUpsellQuickviewTarget,
        quickViewOpen
      );

      return;
    }

    const quickViewClose = event.target.closest(
      '[data-cart-upsell-quickview-close]'
    );

    if (quickViewClose) {
      event.preventDefault();
      event.stopPropagation();

      const quickView =
        quickViewClose.closest(
          '[data-cart-upsell-quickview]'
        ) || this.activeUpsellQuickView;

      this.closeUpsellQuickView(quickView, true);
      return;
    }

    const quickViewThumb = event.target.closest(
      '[data-cart-upsell-quickview-thumb]'
    );

    if (quickViewThumb) {
      event.preventDefault();
      event.stopPropagation();
      this.selectUpsellQuickViewMedia(quickViewThumb);
      return;
    }

    const upsellButton = event.target.closest(
      '[data-cart-upsell-add]'
    );

    if (!upsellButton) return;

    event.preventDefault();

    if (
      upsellButton.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    const variantId = upsellButton.dataset.variantId;

    if (!variantId) return;

    this.addUpsell(variantId, upsellButton);
  }

  onKeyDown(event) {
    if (
      !this.activeUpsellQuickView ||
      event.key !== 'Tab'
    ) {
      return;
    }

    const focusableElements = this.getFocusableElements(
      this.activeUpsellQuickView
    );

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement =
      focusableElements[focusableElements.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === firstElement
    ) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement === lastElement
    ) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  getFocusableElements(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), video[controls], iframe, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (element) =>
        !!(
          element.offsetWidth ||
          element.offsetHeight ||
          element.getClientRects().length
        )
    );
  }

  openUpsellQuickView(targetId, opener) {
    if (!targetId) return;

    const quickView = document.getElementById(targetId);

    if (
      !quickView ||
      !this.contains(quickView)
    ) {
      return;
    }

    if (
      this.activeUpsellQuickView &&
      this.activeUpsellQuickView !== quickView
    ) {
      this.closeUpsellQuickView(
        this.activeUpsellQuickView,
        false
      );
    }

    this.activeUpsellQuickView = quickView;
    this.stopUpsellImpressionTracking();
    this.activeUpsellQuickViewOpener =
      opener || document.activeElement;

    quickView.hidden = false;
    quickView.setAttribute('aria-hidden', 'false');
    quickView.classList.add('is-open');

    this.classList.add('cart-drawer--qv-open');
    document.documentElement.classList.add(
      'cart-drawer-qv-is-open'
    );

    const panel = quickView.querySelector(
      '.cart-drawer__qv-panel'
    );

    const closeButton = quickView.querySelector(
      '[data-cart-upsell-quickview-close]:not(.cart-drawer__qv-backdrop)'
    );

    const openToken = ++this.upsellMediaPlaybackToken;

    window.requestAnimationFrame(() => {
      if (
        openToken !== this.upsellMediaPlaybackToken ||
        this.activeUpsellQuickView !== quickView ||
        quickView.hidden ||
        !quickView.classList.contains('is-open')
      ) {
        return;
      }

      (closeButton || panel || quickView).focus({
        preventScroll: true
      });

      this.playActiveUpsellQuickViewMedia(quickView);
    });

    this.trackUpsellQuickView(
      'td_cart_upsell_quickview_opened',
      quickView
    );
  }

  closeUpsellQuickView(
    quickView,
    restoreFocus = true
  ) {
    if (!quickView) return;

    this.pauseUpsellQuickViewMedia(quickView);

    quickView.classList.remove('is-open');
    quickView.setAttribute('aria-hidden', 'true');
    quickView.hidden = true;

    if (this.activeUpsellQuickView === quickView) {
      this.activeUpsellQuickView = null;
    }

    this.classList.remove('cart-drawer--qv-open');

    document.documentElement.classList.remove(
      'cart-drawer-qv-is-open'
    );

    if (
      restoreFocus &&
      this.activeUpsellQuickViewOpener &&
      typeof this.activeUpsellQuickViewOpener.focus ===
        'function'
    ) {
      this.activeUpsellQuickViewOpener.focus({
        preventScroll: true
      });
    }

    this.activeUpsellQuickViewOpener = null;
    this.observeUpsellImpressions();
  }

  selectUpsellQuickViewMedia(thumb) {
    const quickView = thumb.closest(
      '[data-cart-upsell-quickview]'
    );

    if (
      !quickView ||
      !thumb.dataset.mediaId
    ) {
      return;
    }

    const mediaItems = Array.from(
      quickView.querySelectorAll(
        '[data-cart-upsell-quickview-media]'
      )
    );

    const selectedMedia = mediaItems.find(
      (mediaItem) =>
        mediaItem.dataset.mediaId === thumb.dataset.mediaId
    );

    if (!selectedMedia) return;

    this.pauseUpsellQuickViewMedia(quickView);

    mediaItems.forEach((mediaItem) => {
      const isActive = mediaItem === selectedMedia;

      mediaItem.classList.toggle('is-active', isActive);
      mediaItem.hidden = !isActive;
    });

    const mediaStage = quickView.querySelector(
      '[data-cart-upsell-quickview-stage]'
    );

    if (mediaStage) {
      mediaStage.classList.toggle(
        'is-video-active',
        selectedMedia.dataset.mediaType !== 'image'
      );
    }

    quickView
      .querySelectorAll(
        '[data-cart-upsell-quickview-thumb]'
      )
      .forEach((button) => {
        const isActive = button === thumb;

        button.classList.toggle('is-active', isActive);

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

    this.playUpsellQuickViewMedia(selectedMedia);
  }

  playActiveUpsellQuickViewMedia(quickView) {
    if (!quickView) return;

    const activeMedia = quickView.querySelector(
      '[data-cart-upsell-quickview-media].is-active'
    );

    if (activeMedia) {
      this.playUpsellQuickViewMedia(activeMedia);
    }
  }

  playUpsellQuickViewMedia(mediaItem) {
    if (
      !mediaItem ||
      mediaItem.dataset.mediaType === 'image'
    ) {
      return;
    }

    if (
      this.reducedMotionQuery &&
      this.reducedMotionQuery.matches
    ) {
      return;
    }

    if (this.saveDataEnabled) return;

    const deferredMedia = mediaItem.querySelector(
      '[data-cart-upsell-quickview-deferred]'
    );

    if (!deferredMedia) return;

    if (
      typeof deferredMedia.loadContent === 'function'
    ) {
      deferredMedia.loadContent(false);
    } else {
      const template =
        deferredMedia.querySelector('template');

      if (
        template &&
        !deferredMedia.hasAttribute('loaded')
      ) {
        deferredMedia.appendChild(
          template.content.cloneNode(true)
        );

        deferredMedia.setAttribute('loaded', 'true');
      }
    }

    const playbackToken =
      ++this.upsellMediaPlaybackToken;

    window.requestAnimationFrame(() => {
      const quickView = mediaItem.closest(
        '[data-cart-upsell-quickview]'
      );

      if (
        playbackToken !==
          this.upsellMediaPlaybackToken ||
        !quickView ||
        this.activeUpsellQuickView !== quickView ||
        quickView.hidden ||
        !quickView.classList.contains('is-open') ||
        mediaItem.hidden ||
        !mediaItem.classList.contains('is-active')
      ) {
        return;
      }

      const video =
        deferredMedia.querySelector('video');

      if (video) {
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.controls = true;
        video.preload = 'metadata';

        video.setAttribute('autoplay', 'autoplay');
        video.setAttribute('loop', 'loop');
        video.setAttribute('muted', 'muted');
        video.setAttribute(
          'playsinline',
          'playsinline'
        );
        video.setAttribute(
          'webkit-playsinline',
          'webkit-playsinline'
        );
        video.setAttribute('controls', 'controls');

        const playPromise = video.play();

        if (
          playPromise &&
          typeof playPromise.catch === 'function'
        ) {
          playPromise.catch(() => {});
        }

        return;
      }

      const iframe =
        deferredMedia.querySelector('iframe');

      if (
        !iframe ||
        !iframe.contentWindow
      ) {
        return;
      }

      if (iframe.classList.contains('js-youtube')) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        );
      } else if (
        iframe.classList.contains('js-vimeo')
      ) {
        iframe.contentWindow.postMessage(
          '{"method":"play"}',
          '*'
        );
      }
    });
  }

  pauseUpsellQuickViewMedia(quickView) {
    if (!quickView) return;

    this.upsellMediaPlaybackToken += 1;

    quickView
      .querySelectorAll('video')
      .forEach((video) => video.pause());

    quickView
      .querySelectorAll('iframe')
      .forEach((iframe) => {
        if (
          iframe.contentWindow &&
          iframe.classList.contains('js-youtube')
        ) {
          iframe.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
        } else if (
          iframe.contentWindow &&
          iframe.classList.contains('js-vimeo')
        ) {
          iframe.contentWindow.postMessage(
            '{"method":"pause"}',
            '*'
          );
        }

        const deferredMedia = iframe.closest(
          '[data-cart-upsell-quickview-deferred]'
        );

        iframe.remove();

        if (deferredMedia) {
          deferredMedia.removeAttribute('loaded');
        }
      });
  }

  observeCartDrawerRenders() {
    const cartDrawer = this.querySelector('#CartDrawer');

    if (
      !cartDrawer ||
      typeof window.MutationObserver !== 'function'
    ) {
      return;
    }

    if (this.cartRenderObserver) {
      this.cartRenderObserver.disconnect();
    }

    this.cartRenderObserver = new MutationObserver(
      (mutations) => {
        const offerMarkupChanged = mutations.some(
          (mutation) => {
            if (mutation.type === 'attributes') {
              return mutation.target.matches('[data-cart-upsell-card], [data-cart-upsell-root], [data-td-upsell], .td-cart-panel') ||
                Boolean(mutation.target.querySelector('[data-cart-upsell-card]'));
            }
            if (mutation.type !== 'childList') return false;

            const changedNodes = [
              ...mutation.addedNodes,
              ...mutation.removedNodes
            ];

            return changedNodes.some((node) => {
              if (node.nodeType !== 1) return false;

              return (
                node.matches('[data-cart-upsell-card]') ||
                node.querySelector('[data-cart-upsell-card]')
              );
            });
          }
        );

        if (
          !offerMarkupChanged ||
          !this.classList.contains('active') ||
          document.visibilityState === 'hidden'
        ) {
          return;
        }

        window.requestAnimationFrame(() => {
          if (
            this.classList.contains('active') &&
            document.visibilityState !== 'hidden'
          ) {
            this.observeUpsellImpressions();
          }
        });
      }
    );

    this.cartRenderObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'data-td-suppressed', 'class']
    });
  }
  stopUpsellImpressionTracking() {
    if (this.upsellImpressionObserver) {
      this.upsellImpressionObserver.disconnect();
      this.upsellImpressionObserver = null;
    }

    this.upsellImpressionTimers.forEach((timerId) => {
      window.clearTimeout(timerId);
    });

    this.upsellImpressionTimers.clear();
  }

  resetUpsellImpressionTracking() {
    this.stopUpsellImpressionTracking();
    this.upsellIntersectionRatios = new WeakMap();
    // DOM changes and reopening do not create another exposure for the same cart.
  }

  getUpsellExposureStore() {
    if (window.__tdCartExposuresV2 instanceof Map) return window.__tdCartExposuresV2;
    const store = new Map();
    try {
      const saved = JSON.parse(window.sessionStorage.getItem('td_cart_exposures_v2') || '[]');
      if (Array.isArray(saved)) saved.slice(-100).forEach((entry) => {
        if (Array.isArray(entry) && typeof entry[0] === 'string' &&
          entry[1] && typeof entry[1].id === 'string') {
          store.set(entry[0], { id: entry[1].id, seen: entry[1].seen === true });
        }
      });
    } catch (error) { /* Restricted storage still permits in-page deduplication. */ }
    window.__tdCartExposuresV2 = store;
    return store;
  }

  saveUpsellExposures() {
    while (this.upsellExposures.size > 100) {
      this.upsellExposures.delete(this.upsellExposures.keys().next().value);
    }
    try {
      window.sessionStorage.setItem('td_cart_exposures_v2', JSON.stringify([...this.upsellExposures]));
    } catch (error) { /* Analytics must never block cart interaction. */ }
  }

  getUpsellExposure(card, offerId, variantId) {
    let signature = card ? card.dataset.tdCartSignature || '' : '';
    try {
      const lines = JSON.parse(signature);
      if (Array.isArray(lines)) signature = JSON.stringify(lines.map((line) =>
        [String(line[0]), Number(line[1])]
      ).sort((a, b) => a[0].localeCompare(b[0]) || a[1] - b[1]));
    } catch (error) { /* Older markup can use a plain context string. */ }
    const data = card ? card.dataset : {};
    const key = JSON.stringify([
      data.tdRecommendationVersion || 'legacy', data.tdRule || '',
      data.tdSourceProductId || '', data.tdSourceKey || '',
      signature, offerId, variantId
    ]);
    if (!this.upsellExposures.has(key)) {
      const id = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
      this.upsellExposures.set(key, { id: 'tdcs_' + id, seen: false });
      this.saveUpsellExposures();
    }
    return this.upsellExposures.get(key);
  }

  isUpsellImpressionVisible(card) {
    if (!card || !card.isConnected || !this.contains(card) ||
      !this.classList.contains('active') || this.classList.contains('td-cross-sell-accepted') ||
      this.classList.contains('cart-drawer--qv-open') || document.visibilityState === 'hidden' ||
      this.querySelector('.td-cart-panel:not([hidden])')) return false;
    for (let node = card; node && node !== this.parentElement; node = node.parentElement) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true' ||
        node.getAttribute('data-td-suppressed') === 'true') return false;
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' ||
        style.visibility === 'collapse' || style.opacity === '0') return false;
    }
    return true;
  }

  getUpsellTrackingContext(target, extra = {}) {
    const card =
      target && typeof target.closest === 'function'
        ? target.closest('[data-cart-upsell-card]')
        : null;

    const variantButton =
      target &&
      typeof target.matches === 'function' &&
      target.matches('[data-cart-upsell-add]')
        ? target
        : card
          ? card.querySelector('[data-cart-upsell-add]')
          : null;

    const variantId = String(
      extra.td_offer_variant_id ||
        (variantButton && variantButton.dataset.variantId) ||
        ''
    );

    const productHandle = card
      ? card.dataset.cartUpsellProductHandle || ''
      : '';
    const data = card ? card.dataset : {};
    const offerId = productHandle || (variantId ? 'variant_' + variantId : 'unknown');
    // The exposure belongs to the offer as displayed, even when its selector chooses a variant.
    const displayedVariantId = card && card.querySelector('[data-cart-upsell-add]')
      ? card.querySelector('[data-cart-upsell-add]').dataset.variantId || variantId
      : variantId;
    const exposure = this.getUpsellExposure(card, offerId, String(displayedVariantId));
    const priceCents = Number(data.tdOfferPrice);
    const cartCents = Number(data.tdCartTotal);

    return {
      td_add_source: 'cart_cross_sell',
      td_placement: 'cart_drawer',
      td_offer_id: offerId,
      td_offer_variant_id: variantId,
      td_event_version: '2',
      td_exposure_id: exposure.id,
      td_source_product_id: data.tdSourceProductId || '',
      td_rule: data.tdRule || 'legacy',
      td_offer_version: data.tdRecommendationVersion || 'legacy',
      td_offer_type: data.tdOfferType || 'cross_sell',
      currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active || 'CLP' : 'CLP',
      ...(data.tdCartTotal !== undefined && Number.isFinite(cartCents) ? { td_cart_total_before: cartCents / 100 } : {}),
      ...(data.tdOfferPrice !== undefined && Number.isFinite(priceCents) ? { td_incremental_value: priceCents / 100 } : {}),
      product_handle: productHandle,
      product_title: card
        ? card.dataset.cartUpsellProductTitle || ''
        : '',
      ...extra
    };
  }

  publishUpsellEvent(eventName, payload) {
    try { this.publishUpsellEventSafe(eventName, payload); } catch (error) { console.warn('Cart analytics unavailable', error); }
  }

  publishUpsellEventSafe(eventName, payload) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    } else {
      window.dataLayer = window.dataLayer || [];

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: eventName,
          ...payload
        });
      }
    }

    if (typeof window.clarity === 'function') {
      window.clarity(
        'set',
        'td_cs_placement',
        payload.td_placement
      );

      if (payload.td_offer_id !== 'unknown') {
        window.clarity(
          'set',
          'td_cs_offer',
          payload.td_offer_id
        );
      }

      if (payload.td_source_product_id) window.clarity('set', 'td_cs_source', String(payload.td_source_product_id));
      if (payload.td_rule) window.clarity('set', 'td_cs_rule', payload.td_rule);
      if (payload.td_offer_version) window.clarity('set', 'td_cs_version', payload.td_offer_version);

      if (eventName === 'td_cross_sell_impression') {
        window.clarity('set', 'td_cs_seen', 'yes');
      }

      if (eventName === 'td_cross_sell_added') {
        window.clarity('set', 'td_cs_accepted', 'yes');
      }

      if (eventName === 'td_cross_sell_removed' || eventName === 'td_cross_sell_undone') {
        window.clarity('set', 'td_cs_removed', 'yes');
      }

      window.clarity('event', eventName);
    }

    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        const publishResult =
          window.Shopify.analytics.publish(
            'the_dogs:' + eventName,
            payload
          );

        if (
          publishResult &&
          typeof publishResult.catch === 'function'
        ) {
          publishResult.catch(() => {});
        }
      } catch (error) {
        console.warn(
          'Shopify analytics publish failed',
          error
        );
      }
    }
  }

  observeUpsellImpressions() {
    this.stopUpsellImpressionTracking();
    this.upsellIntersectionRatios = new WeakMap();
    if (!this.classList.contains('active') || document.visibilityState === 'hidden' ||
      typeof window.IntersectionObserver !== 'function') return;

    const cards = Array.from(this.querySelectorAll('[data-cart-upsell-card]'));
    if (!cards.length) return;
    const alreadySeen = (id) => [...this.upsellExposures.values()].some((entry) => entry.id === id && entry.seen);
    this.upsellImpressionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        this.upsellIntersectionRatios.set(card, entry.intersectionRatio);
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5 &&
          this.isUpsellImpressionVisible(card);
        if (!visible) {
          const timer = this.upsellImpressionTimers.get(card);
          if (timer !== undefined) window.clearTimeout(timer);
          this.upsellImpressionTimers.delete(card);
          return;
        }
        const context = this.getUpsellTrackingContext(card);
        if (alreadySeen(context.td_exposure_id)) {
          this.upsellImpressionObserver?.unobserve(card);
          return;
        }
        if (this.upsellImpressionTimers.has(card)) return;
        const timer = window.setTimeout(() => {
          this.upsellImpressionTimers.delete(card);
          if (!this.isUpsellImpressionVisible(card) ||
            (this.upsellIntersectionRatios.get(card) || 0) < 0.5) return;
          const latest = this.getUpsellTrackingContext(card);
          // Changed offers need their own full continuous second on screen.
          if (latest.td_exposure_id !== context.td_exposure_id) {
            this.observeUpsellImpressions();
            return;
          }
          if (alreadySeen(latest.td_exposure_id)) return;
          const exposure = [...this.upsellExposures.values()].find((item) => item.id === latest.td_exposure_id);
          if (!exposure) return;
          exposure.seen = true;
          this.saveUpsellExposures();
          this.upsellImpressionObserver?.unobserve(card);
          this.publishUpsellEvent('td_cross_sell_impression', {
            ...latest,
            td_viewability: '50_percent_1_second'
          });
        }, 1000);
        this.upsellImpressionTimers.set(card, timer);
      });
    }, { root: this.querySelector('.drawer__inner'), threshold: [0, 0.5, 1] });
    cards.forEach((card) => this.upsellImpressionObserver.observe(card));
  }

  trackUpsellQuickView(eventName, quickView, extra = {}) {
    this.publishUpsellEvent(eventName, this.getUpsellTrackingContext(quickView, extra));
  }

  addUpsell(variantId, button) {
    if (window.TDCart) return window.TDCart.add(variantId, button);
    // Never submit an uncoordinated request if the editorial controller failed to load.
    const feedback = this.querySelector('#td-cart-feedback');
    if (feedback) feedback.textContent = 'No pudimos cargar el carrito. Actualiza la página para continuar.';
    return;
  }

  renderContents(parsedState) {
    const previousScroll = this.querySelector('cart-drawer-items')?.scrollTop || 0;
    if (this.activeUpsellQuickView) {
      this.pauseUpsellQuickViewMedia(
        this.activeUpsellQuickView
      );
    }

    this.activeUpsellQuickView = null;
    this.activeUpsellQuickViewOpener = null;

    this.classList.remove('cart-drawer--qv-open');

    document.documentElement.classList.remove(
      'cart-drawer-qv-is-open'
    );

    this.classList.remove('is-empty');

    const drawerInner =
      this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId =
      parsedState.id ||
      (parsedState.items &&
        parsedState.items[0] &&
        parsedState.items[0].id);

    const sections = parsedState.sections || {};

    this.getSectionsToRender().forEach((section) => {
      const html = sections[section.id];

      if (!html) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const nextHtml = this.getSectionInnerHTML(
        html,
        section.selector
      );

      if (nextHtml !== null) {
        sectionElement.innerHTML = nextHtml;
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector(
        '#CartDrawer-Overlay'
      );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
      const scroll = this.querySelector('cart-drawer-items');
      if (scroll) scroll.scrollTop = previousScroll;
      window.TDCart?.sync();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return null;

    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element =
      parsed.querySelector(selector) ||
      parsed.querySelector('.shopify-section');

    return element ? element.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);